import { G, SPEED_OF_LIGHT } from '../constants';

/**
 * engines/relativity
 *
 * シュヴァルツシルト時空（回転・電荷を持たない球対称なブラックホール／コンパクト天体）
 * に関する基礎的な一般相対性理論の計算。engines/orbital のニュートン力学に対する
 * 「補正」または「極限での置き換え」として使う。
 *
 * 適用範囲の注意:
 * - ここで扱うのはシュヴァルツシルト解（非回転・無電荷）のみ。実在のブラックホールは
 *   角運動量を持つ（カー解）ことがほとんどだが、MVPでは扱わない。
 * - 近日点移動の式は「1PN近似」（弱い重力場での最低次の補正）であり、強い重力場
 *   （r が r_s の数倍程度）では精度が落ちる。教育目的の近似として扱うこと。
 */

/**
 * シュヴァルツシルト半径 r_s [m]: これより内側からは光さえ脱出できない事象の地平面。
 * r_s = 2GM/c²
 */
export function schwarzschildRadius(massKg: number): number {
  if (massKg <= 0) throw new Error('massKg must be positive');
  return (2 * G * massKg) / (SPEED_OF_LIGHT * SPEED_OF_LIGHT);
}

/**
 * 光子球半径 [m]: 光がちょうど円軌道を描く半径。r_photon = 1.5 r_s
 * （これより内側では光さえも安定な円軌道を保てない）
 */
export function photonSphereRadius(massKg: number): number {
  return 1.5 * schwarzschildRadius(massKg);
}

/**
 * ISCO（最内安定円軌道, Innermost Stable Circular Orbit）半径 [m]。
 * r_isco = 3 r_s = 6GM/c²
 * これより内側では、質量を持つ物体は安定な円軌道を維持できずブラックホールに落下する。
 * 降着円盤の内縁はおおよそこの半径で切れる。
 */
export function iscoRadius(massKg: number): number {
  return 3 * schwarzschildRadius(massKg);
}

/**
 * 重力による時間の遅れの係数（プロパー時間 / 遠方観測者の座標時間）。
 * dτ/dt = √(1 − r_s/r)
 *
 * r <= r_s では定義できない（事象の地平面の内側）。
 */
export function timeDilationFactor(radiusM: number, massKg: number): number {
  const rs = schwarzschildRadius(massKg);
  if (radiusM <= rs) {
    throw new Error('radiusM must be greater than the Schwarzschild radius (outside the event horizon)');
  }
  return Math.sqrt(1 - rs / radiusM);
}

/**
 * 重力赤方偏移 z = 1/√(1 − r_s/r) − 1
 * 遠方観測者から見て、半径rで放射された光の波長がどれだけ伸びるか。
 */
export function gravitationalRedshift(radiusM: number, massKg: number): number {
  const rs = schwarzschildRadius(massKg);
  if (radiusM <= rs) {
    throw new Error('radiusM must be greater than the Schwarzschild radius (outside the event horizon)');
  }
  return 1 / Math.sqrt(1 - rs / radiusM) - 1;
}

/**
 * 近日点移動（1PN近似）: 1公転あたりの近点引数の歳差 Δφ [rad/orbit]。
 * Δφ = 6πGM / (c² a (1 − e²))
 *
 * 水星の近日点移動（GR起因分、約43秒角/世紀）の再現に使われる、一般相対性理論の
 * 古典的な検証式。弱い重力場での1次近似なので、コンパクトな軌道（a が r_s の
 * 数倍程度）では誤差が大きくなる。
 */
export function perihelionPrecessionPerOrbitRad(
  semiMajorAxisM: number,
  eccentricity: number,
  massKg: number,
): number {
  if (semiMajorAxisM <= 0) throw new Error('semiMajorAxisM must be positive');
  if (eccentricity < 0 || eccentricity >= 1) throw new Error('eccentricity must be in [0, 1)');
  const c2 = SPEED_OF_LIGHT * SPEED_OF_LIGHT;
  return (6 * Math.PI * G * massKg) / (c2 * semiMajorAxisM * (1 - eccentricity * eccentricity));
}

export type SpacetimeRegion = 'far-field' | 'accretion-disk' | 'photon-sphere-crossing' | 'inside-isco' | 'inside-horizon';

/**
 * 与えられた半径がブラックホール周辺のどの領域にあるかを分類する。
 * UI側で「降着円盤の内縁」「光子球」などの視覚的な帯を描き分けるのに使う想定。
 */
export function classifySpacetimeRegion(radiusM: number, massKg: number): SpacetimeRegion {
  const rs = schwarzschildRadius(massKg);
  if (radiusM <= rs) return 'inside-horizon';
  if (radiusM <= photonSphereRadius(massKg)) return 'photon-sphere-crossing';
  if (radiusM <= iscoRadius(massKg)) return 'inside-isco';
  if (radiusM <= iscoRadius(massKg) * 10) return 'accretion-disk';
  return 'far-field';
}
